import React, { useEffect, useId, useState } from 'react';
import { FaTimes } from 'react-icons/fa';
import { useLocale } from '../../../contexts/LocaleContext.jsx';
import '../../../styles/LiveMomentCard.css';

const secondsLeft = (endsAt, offsetMs) => {
  const end = Date.parse(endsAt);
  if (!Number.isFinite(end)) return 0;
  return Math.max(0, Math.ceil((end - (Date.now() + offsetMs)) / 1000));
};

/** One Yes/No row of the rating; the chosen side shows as selected. */
const RatingRow = ({ label, value, disabled, onRate, testId }) => {
  const { t } = useLocale();
  return (
    <div className="rating-row" role="group" aria-label={label} data-testid={testId}>
      <span className="rating-row-label">{label}</span>
      <div className="rating-row-choices">
        {[true, false].map((liked) => (
          <button
            key={String(liked)}
            type="button"
            className={`rating-choice${value === liked ? ' rating-choice--on' : ''}`}
            aria-pressed={value === liked}
            disabled={disabled}
            onClick={() => onRate(liked)}
          >
            {liked ? t('stream.rating.yes') : t('stream.rating.no')}
          </button>
        ))}
      </div>
    </div>
  );
};

/**
 * The AFTER THE SONG card (mockup 14, ENG-693) in the performer card's place
 * when a song finishes: did you like the song, and the artist? Each row is
 * answered on its own and can be changed while the card is open; the
 * countdown shows "20s left · 88 voted". It closes when the rating ends.
 */
const RatingCard = ({ moment, votes = {}, result, answered, offsetMs = 0, onRate, onDismiss }) => {
  const { t, format } = useLocale();
  const headingId = useId();
  const [left, setLeft] = useState(() => (moment ? secondsLeft(moment.endsAt, offsetMs) : 0));

  useEffect(() => {
    if (!moment || result) return undefined;
    setLeft(secondsLeft(moment.endsAt, offsetMs));
    const timer = setInterval(() => setLeft(secondsLeft(moment.endsAt, offsetMs)), 250);
    return () => clearInterval(timer);
  }, [moment, offsetMs, result]);

  if (!moment) return null;
  const closed = Boolean(result) || left === 0;
  const total = Math.max(1, Math.round((Date.parse(moment.endsAt) - Date.parse(moment.startsAt)) / 1000) || 20);
  const progress = closed ? 0 : Math.min(1, left / total);
  const voted = { count: answered, formattedCount: format.number(answered) };

  return (
    <section className="live-moment" data-testid="rating-card" aria-labelledby={headingId}>
      <header className="live-moment-header">
        <span className="live-moment-kicker">{t('stream.rating.kicker')}</span>
        <button type="button" className="live-moment-close" onClick={onDismiss} aria-label={t('stream.rating.close')} data-testid="rating-close">
          <FaTimes aria-hidden="true" />
        </button>
      </header>
      <h2 className="live-moment-question" id={headingId}>{t('stream.rating.title')}</h2>
      <RatingRow
        label={t('stream.rating.song', { title: moment.songTitle || t('stream.rating.thisSong') })}
        value={votes.song}
        disabled={closed}
        onRate={(liked) => onRate('song', liked)}
        testId="rating-song"
      />
      <RatingRow
        label={t('stream.rating.artist', { artist: moment.artist || t('stream.rating.thisArtist') })}
        value={votes.artist}
        disabled={closed}
        onRate={(liked) => onRate('artist', liked)}
        testId="rating-artist"
      />
      <div className="live-moment-bar" aria-hidden="true">
        <span className="live-moment-bar-fill" style={{ inlineSize: `${progress * 100}%` }} />
      </div>
      <p className="live-moment-status" role="status" aria-live="polite" data-testid="rating-status">
        {result
          ? t('stream.rating.thanks', voted)
          : t('stream.rating.countdown', { seconds: format.number(left), ...voted })}
      </p>
    </section>
  );
};

export default RatingCard;
